import { useEffect, useMemo, useState } from 'react'
import ReferralLinkCard from '../../../components/ambassador/ReferralLinkCard'
import AmbassadorHowItWorks from '../../../components/ambassador/AmbassadorHowItWorks'
import AmbassadorStatusCard from '../../../components/ambassador/AmbassadorStatusCard'
import AmbassadorActivityList from '../../../components/ambassador/AmbassadorActivityList'
import AmbassadorFAQ from '../../../components/ambassador/AmbassadorFAQ'
import AmbassadorWhatYouEarn from '../../../components/ambassador/AmbassadorWhatYouEarn'
import ShareKit from '../../../components/ambassador/ShareKit'
import { getAmbassadorEarnings, type AmbassadorResponse } from '../../../api/earnings/ambassador'

function money(value: string): string {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return value
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export default function AmbassadorPage() {
  const [data, setData] = useState<AmbassadorResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getAmbassadorEarnings()
      setData(res)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load ambassador earnings.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const tierRateRows = useMemo(() => data?.rates?.tiers || [], [data])

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-44 rounded-2xl border border-white/10 bg-white/5" />
        <div className="h-28 rounded-2xl border border-white/10 bg-white/5" />
        <div className="h-28 rounded-2xl border border-white/10 bg-white/5" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="rounded-2xl border border-red-400/20 bg-red-500/10 p-5">
        <div className="font-semibold text-red-200">Could not load Ambassador program</div>
        <div className="mt-1 text-sm text-red-100/80">{error || 'Unknown error'}</div>
        <button type="button" onClick={() => void load()} className="mt-3 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-sm hover:bg-white/15">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <ReferralLinkCard referralUrl={data.referral.url} referralCode={data.referral.code} />

      <ShareKit referralUrl={data.referral.url} handle={data.me.handle} />

      <AmbassadorHowItWorks />

      <AmbassadorWhatYouEarn protocolFeeBps={data.rates.protocol_fee_bps} tiers={tierRateRows} />

      <AmbassadorStatusCard status={data.status} />

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="text-xs text-white/60">This month earned</div>
          <div className="mt-1 text-xl font-semibold text-emerald-200">{money(data.summary.earned_month)} PIXE</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="text-xs text-white/60">Lifetime earned</div>
          <div className="mt-1 text-xl font-semibold text-cyan-100">{money(data.summary.earned_lifetime)} PIXE</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="text-xs text-white/60">Pending</div>
          <div className="mt-1 text-xl font-semibold text-white">{money(data.summary.pending || '0.00')} PIXE</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="text-xs text-white/60">Referred users active</div>
          <div className="mt-1 text-xl font-semibold text-white">{data.summary.active_referrals}</div>
        </div>
      </section>

      <AmbassadorActivityList rows={data.recent_activity} />

      <AmbassadorFAQ />

      <div className="text-xs text-white/50">
        Ambassador rewards are paid from protocol fees and may change over time. Fraud, spam, or policy violations may pause or revoke rewards.
      </div>
    </div>
  )
}
